import React, { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, ButtonVariant } from '../../../../common/Button'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { TextInput } from '../../../../common/TextInput'
import { useFormErrorMessage } from '../../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../../common/FormControl'
import { societyRequestSchema } from '../../../../../forms/schemas/society_request'
import { UnitRole } from '../../../../../types/unit-roles'
import { FormSpacing } from '../../../../common/FormSpacing'
import { getUnitRole } from '../../../../../helpers/translations/unit-roles'
import { SelectDropdown, SelectOption } from '../../../../common/SelectDropdown'
import { TextArea } from '../../../../common/TextArea'
import { IRequestModel } from '../../../../../interfaces/models/requests.interfaces'
import { IUnitModel } from '../../../../../interfaces/models/units.interfaces'
import { IUserModel } from '../../../../../interfaces/models/users.interfaces'
import { ISocietyEntranceModel } from '../../../../../interfaces/models/society-entrance.interfaces'
import { getUserFullName } from '../../../../../helpers/user'
import { ISocietiesModel } from '../../../../../interfaces/models/societies.interfaces'
import { i18nSocietyType } from '../../../../../i18n'

interface RequestFormProps {
  onError: () => void
  onApprove: (data: InferType<typeof societyRequestSchema>) => Promise<void>
  onDeny: () => Promise<void>
  loading: boolean
  request: IRequestModel
  requestUnit: IUnitModel
  user: IUserModel
  societyEntrances: ISocietyEntranceModel[]
  society: ISocietiesModel
  societyUnits: IUnitModel[]
}

export const RequestForm = ({
  request,
  loading,
  onError,
  onApprove,
  onDeny,
  requestUnit,
  user,
  societyEntrances,
  societyUnits,
  society,
}: RequestFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()

  const getDefaultValues = useCallback((): InferType<
    typeof societyRequestSchema
  > => {
    return {
      unitId: request?.unitId || '',
      entranceId: requestUnit?.entranceId || '',
      role: request?.role || UnitRole.MEMBER,
    }
  }, [request, requestUnit])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyRequestSchema),
    defaultValues: getDefaultValues(),
  })

  const watchEntranceId = watch('entranceId')

  const entranceOptions = (): SelectOption[] =>
    societyEntrances.map((entrance) => ({
      label: entrance.addressStreet as string,
      value: entrance._id,
    }))

  const entranceUnitOptions = (): SelectOption[] =>
    society
      ? societyUnits
          .filter((unit) => unit.entranceId === watchEntranceId)
          .map((unit) => {
            if (unit.isUnassigned) {
              return {
                value: unit._id,
                label: translate('common.unit.unassigned'),
              }
            }
            return {
              value: unit._id,
              label: `${unit.titleLegal} (${unit.title})`,
            }
          })
      : []

  const unitRoleOptions = (): SelectOption[] =>
    Object.values(UnitRole).map((val) => ({
      value: val as string,
      label: translate(getUnitRole(val) as string),
    }))

  return (
    <FormSpacing>
      <FormControl
        label={translate('common.form.labels.name')}
        name={translate('common.form.labels.name')}
      >
        <TextInput
          value={getUserFullName(user)}
          disabled
          onChange={() => null}
          onBlur={() => null}
        />
      </FormControl>
      <FormControl
        label={translate('common.form.labels.email')}
        name={translate('common.form.labels.email')}
      >
        <TextInput
          value={user.email}
          disabled
          onChange={() => null}
          onBlur={() => null}
        />
      </FormControl>
      {request.message && (
        <FormControl
          label={translate('common.form.labels.message')}
          name={translate('common.form.labels.message')}
        >
          <TextArea
            value={request.message}
            disabled
            onChange={() => null}
            onBlur={() => null}
          />
        </FormControl>
      )}
      <Controller
        control={control}
        name="entranceId"
        render={({ field: { value, name, onChange } }) => (
          <FormControl
            label={translate('registerView.unit.labels.entrance', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
            error={errors.entranceId && getErrorMessage(errors.entranceId)}
            name={name}
          >
            <SelectDropdown
              options={entranceOptions()}
              value={entranceOptions().find((option) => option.value === value)}
              onChange={(option) => {
                onChange(option?.value)
                setValue('unitId', '', { shouldValidate: true })
              }}
              error={!!errors.entranceId}
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="unitId"
        render={({ field: { value, name, onChange } }) => (
          <FormControl
            label={translate('common.singularPlural.unit.singular', {
              context: i18nSocietyType(society?.societyTypeDisplayName),
            })}
            error={errors.unitId && getErrorMessage(errors.unitId)}
            name={name}
          >
            <SelectDropdown
              options={entranceUnitOptions()}
              value={entranceUnitOptions().find(
                (option) => option.value === value
              )}
              onChange={(option) => onChange(option?.value)}
              error={!!errors.unitId}
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="role"
        render={({ field: { value, name, onChange } }) => (
          <FormControl
            label={translate('common.form.labels.role')}
            error={errors.role && getErrorMessage(errors.role)}
            name={name}
          >
            <SelectDropdown
              options={unitRoleOptions()}
              value={unitRoleOptions().find((option) => option.value === value)}
              onChange={(option) => onChange(option?.value)}
              error={!!errors.role}
            />
          </FormControl>
        )}
      />
      <div className="mt-2 flex items-center gap-x-3">
        <Button
          label={translate('common.actions.deny')}
          variant={ButtonVariant.DANGER}
          disabled={loading}
          onClick={onDeny}
        />
        <Button
          label={translate('common.actions.accept')}
          variant={ButtonVariant.PRIMARY}
          type="submit"
          disabled={!isValid || loading}
          onClick={handleSubmit(onApprove, onError)}
        />
      </div>
    </FormSpacing>
  )
}
